"use client";

import { useEffect } from "react";

/**
 * ChunkReloadGuard — recovers from post-deploy "Loading chunk … failed"
 * errors.
 *
 * Next.js splits the app into content-hashed chunks. When a new version is
 * deployed, the hashes change and the old chunk files are eventually removed
 * from the CDN. A user who had the page open across the deploy (or who
 * navigates to a route whose chunk is requested lazily) then asks for a chunk
 * URL that no longer exists → ChunkLoadError ("Loading chunk 9771 failed").
 * The page is wedged because the code it needs is gone.
 *
 * The fix is a single hard reload, which re-fetches the current HTML and its
 * up-to-date chunk hashes. We reload AT MOST ONCE PER SESSION (guarded by a
 * sessionStorage flag) so a genuinely unreachable chunk — offline, CDN
 * outage — can never put the tab into a reload loop. If the reload fixes it
 * (the normal case) the user never notices; if it doesn't, they see the error
 * once and we stop.
 */
export function ChunkReloadGuard() {
  useEffect(() => {
    const FLAG = "snap-site:chunk-reloaded";

    const isChunkError = (msg?: string | null) =>
      !!msg &&
      /Loading chunk [\w-]+ failed|Loading CSS chunk [\w-]+ failed|ChunkLoadError|error loading dynamically imported module|importing a module script failed/i.test(
        msg
      );

    const reloadOnce = () => {
      try {
        if (sessionStorage.getItem(FLAG)) return; // already reloaded this session
        sessionStorage.setItem(FLAG, String(Date.now()));
      } catch {
        // sessionStorage unavailable (private mode, etc.) — skip the guard
        // rather than risk a loop, and don't reload.
        return;
      }
      window.location.reload();
    };

    const onError = (e: ErrorEvent) => {
      if (isChunkError(e.message) || isChunkError(e.error?.message)) reloadOnce();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const r = e.reason as { name?: string; message?: string } | string | undefined;
      const msg = typeof r === "string" ? r : r?.message;
      if (isChunkError(msg) || (typeof r === "object" && r?.name === "ChunkLoadError")) {
        reloadOnce();
      }
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
